// EIP-1193 wallet connection shared by every venue that needs a browser-wallet
// signature or transaction (Hyperliquid's approveAgent, Derive's session-key
// registration). Routed through the AppKit-managed wagmi connection instead of
// a raw `window.ethereum` lookup so every wallet-connect UI entry point shares
// one connection state.
//
// `@wagmi/core` + `@/lib/configs/wallet-config` (which pulls in @reown/appkit,
// wagmi and viem) are loaded via a dynamic `import()` rather than a static
// top-level one. This is the single chokepoint every wallet-connect UI entry
// point funnels through, so deferring it here keeps this whole dependency
// graph out of the main bundle/build until a user actually triggers a wallet
// action. The browser caches the resolved module after the first call, so
// repeated calls don't re-fetch.
async function loadWallet() {
  const [
    { disconnect, getAccount, getChainId: getWagmiChainId, watchAccount },
    { appKit, appKitReady, wagmiConfig },
  ] = await Promise.all([import('@wagmi/core'), import('@/lib/configs/wallet-config')])
  return { disconnect, getAccount, getWagmiChainId, watchAccount, appKit, appKitReady, wagmiConfig }
}

/**
 * Warm the wallet graph (and let AppKit discover injected wallets) as soon as a
 * wallet-capable surface opens, so the first Connect click isn't paying for the
 * download. Nothing loads it at app boot — it would sit on every page load's
 * critical path.
 */
export function preloadWallet() {
  void loadWallet().catch(() => {})
}

export type Eip1193Provider = {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>
}

const WALLET_CONNECT_TIMEOUT_MS = 120_000
const MANUAL_WALLET_ACTION_TTL_MS = 10 * 60_000

let manualWalletActionExpiresAt = 0

function hasUserActivationApi(): boolean {
  return typeof navigator !== 'undefined' && 'userActivation' in navigator
}

function hasTransientUserActivation(): boolean {
  if (!hasUserActivationApi()) return true
  return Boolean(navigator.userActivation?.isActive)
}

function markManualWalletAction() {
  manualWalletActionExpiresAt = Date.now() + MANUAL_WALLET_ACTION_TTL_MS
}

function hasManualWalletActionSession(): boolean {
  if (!hasUserActivationApi()) return true
  return Date.now() <= manualWalletActionExpiresAt
}

function assertManualWalletConnect() {
  if (hasTransientUserActivation()) return
  throw new Error('Wallet connection must be started from a user action.')
}

export function assertManualWalletAction() {
  if (hasManualWalletActionSession()) return
  throw new Error('Wallet signing must be started from a user action.')
}

/**
 * Open a manual-wallet-action window from a click handler, for flows that sign
 * with an ALREADY-connected wallet.
 *
 * The signing session is otherwise only ever opened by `connectWallet`, so a
 * flow that skips the connect step (because the wallet is already connected)
 * hits `assertManualWalletAction`'s "must be started from a user action" even
 * though a real click started it. That failed intermittently rather than
 * always: it depended on whether wagmi had rehydrated the previous session
 * yet, so a page refresh appeared to "fix" it.
 *
 * Still requires transient user activation, so it can only succeed inside a
 * genuine gesture — call it synchronously at the top of the handler, before
 * any await, since activation does not survive one.
 */
export function beginManualWalletAction() {
  assertManualWalletConnect()
  markManualWalletAction()
}

function waitForWalletConnection(wallet: Awaited<ReturnType<typeof loadWallet>>): Promise<string> {
  const { getAccount, wagmiConfig, watchAccount, appKit } = wallet

  return new Promise((resolve, reject) => {
    let settled = false
    let unwatchAccount = () => {}
    let unsubscribeAppKit = () => {}
    let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined

    const cleanup = () => {
      unwatchAccount()
      unsubscribeAppKit()
      if (timeoutId !== undefined) {
        globalThis.clearTimeout(timeoutId)
      }
    }

    const settle = (callback: () => void) => {
      if (settled) return
      settled = true
      cleanup()
      callback()
    }

    const getAuthorizedWalletAddress = (): string | null => {
      const account = getAccount(wagmiConfig)
      return account.isConnected && account.address ? account.address : null
    }

    const resolveConnectedAccount = () => {
      const address = getAuthorizedWalletAddress()
      if (address) {
        settle(() => resolve(address))
        return true
      }
      return false
    }

    unwatchAccount = watchAccount(wagmiConfig, {
      onChange() {
        resolveConnectedAccount()
      },
    })

    unsubscribeAppKit = appKit.subscribeState((state) => {
      if (state.open) return
      if (resolveConnectedAccount()) return
      settle(() => reject(new Error('No wallet account was authorized.')))
    })

    timeoutId = globalThis.setTimeout(() => {
      settle(() => reject(new Error('Wallet authorization timed out.')))
    }, WALLET_CONNECT_TIMEOUT_MS)

    resolveConnectedAccount()
  })
}

async function providerFromWallet(
  wallet: Awaited<ReturnType<typeof loadWallet>>
): Promise<Eip1193Provider> {
  const account = wallet.getAccount(wallet.wagmiConfig)
  const provider = (await account.connector?.getProvider()) as Eip1193Provider | undefined

  if (!provider) {
    throw new Error('No wallet provider was authorized.')
  }

  return provider
}

export async function getWalletProvider(): Promise<Eip1193Provider> {
  const wallet = await loadWallet()
  return providerFromWallet(wallet)
}

/**
 * The already-connected wallet address, or null if none — never prompts and
 * never disconnects.
 *
 * `connectWallet` deliberately tears down the existing session and forces the
 * picker, which is right when the user is choosing which wallet to onboard.
 * Flows acting on an ALREADY-linked account (e.g. approving a fee for it) must
 * not do that: throwing a wallet-selection modal at someone who is already
 * connected reads as an error, and disconnecting them as a side effect of
 * opening a dialog is worse.
 */
export async function getConnectedAddress(): Promise<string | null> {
  const wallet = await loadWallet()
  return wallet.getAccount(wallet.wagmiConfig).address ?? null
}

/**
 * Open the AppKit modal and return the connected master account address.
 * Always shows the picker rather than silently reusing a restored session —
 * this is only ever invoked from an explicit user action (onboard or
 * re-authorize button), so the user should get to choose/switch wallets
 * every time instead of being routed straight back into whichever connector
 * was used last.
 */
export async function connectWallet(): Promise<string> {
  assertManualWalletConnect()
  const wallet = await loadWallet()
  const { disconnect, getAccount, wagmiConfig, appKit, appKitReady } = wallet
  // Remote project config applied and SIWX forced off (see wallet-config) — otherwise the
  // modal could end on a "Sign In" message step we never asked for.
  await appKitReady

  const existingAccount = getAccount(wagmiConfig)
  if (existingAccount.connector) {
    await disconnect(wagmiConfig, { connector: existingAccount.connector })
  }

  // 'Connect' auto-skips straight to the only detected injected wallet
  // (e.g. MetaMask) instead of showing the picker. 'AllWallets' always shows
  // the full connector list so the user can choose.
  await appKit.open({ view: 'AllWallets' })

  const address = await waitForWalletConnection(wallet)
  markManualWalletAction()
  return address
}

/**
 * Read the wallet's currently-active EVM chain id. MetaMask refuses to sign
 * an EIP-712 payload (or send a transaction) whose target chain differs from
 * the active chain, so callers that need a specific chain should switch to it
 * first (see `lib/derive/sign-session-key.ts`).
 */
export async function getChainId(): Promise<number> {
  assertManualWalletAction()
  const wallet = await loadWallet()
  const provider = await providerFromWallet(wallet)
  const chainId = await provider.request({ method: 'eth_chainId' })

  if (typeof chainId === 'string') {
    const parsedChainId = Number.parseInt(chainId, chainId.startsWith('0x') ? 16 : 10)
    if (Number.isFinite(parsedChainId)) {
      return parsedChainId
    }
  }

  if (typeof chainId === 'number') {
    return chainId
  }

  return wallet.getWagmiChainId(wallet.wagmiConfig)
}

/**
 * Point the wallet at `chainId` before a signature that is domain-separated
 * on it. A mismatch is not an error the user can see — MetaMask silently
 * refuses the typed-data prompt — so this has to happen before the request,
 * not in response to a failure.
 *
 * No-op when the wallet is already there. Rejections propagate untouched.
 *
 * Configured networks go through AppKit's adapter, which adds missing chains
 * and keeps its network state in sync with the wallet. Other chains retain
 * the provider-level switch used by existing venue flows.
 */
export async function switchWalletChain(chainId: number): Promise<void> {
  assertManualWalletAction()
  if ((await getChainId()) === chainId) return

  const wallet = await loadWallet()
  const network = wallet.wagmiConfig.chains.find((chain) => chain.id === chainId)
  if (network) {
    await wallet.appKitReady
    await wallet.appKit.switchNetwork(network, { throwOnFailure: true })
    return
  }
  const provider = await providerFromWallet(wallet)
  await provider.request({
    method: 'wallet_switchEthereumChain',
    params: [{ chainId: `0x${chainId.toString(16)}` }],
  })
}
